import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { AlertTriangle, Clock, Package, Loader2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { WaSendButton } from "./WaSendButton";
import { useLang } from "../i18n/LanguageContext";

export const PerfAlertsPanel = () => {
  const { t, dir } = useLang();
  const [data, setData] = useState(null);
  const ALERT_COLS = [t("dash.perfAlerts.colRider"), t("dash.perfAlerts.colBranch"), t("dash.perfAlerts.colHours"), t("dash.perfAlerts.colRejected"), t("dash.perfAlerts.colCompleted"), t("dash.perfAlerts.colBreach")];
  useEffect(() => {
    api.ridersFlagged().then(setData).catch(() => toast.error(t("dash.perfAlerts.loadFail")));
  }, [t]);

  if (!data) return <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const flagged = data.flagged || [];

  return (
    <div className="space-y-4" data-testid="perf-alerts-panel" dir={dir}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> {t("dash.perfAlerts.title")}</h3>
        {data.date && <span className="text-xs text-slate-400">{t("dash.perfAlerts.lastDay")} <span className="font-mono-nums" dir="ltr">{data.date}</span></span>}
      </div>
      {flagged.length === 0 ? (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 py-12 text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-emerald-400" />
          <p className="mt-2 font-heading font-bold text-emerald-700">{t("dash.perfAlerts.noneTitle")}</p>
          <p className="text-xs text-emerald-600 mt-1">{t("dash.perfAlerts.noneSub")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <div className="min-w-[560px] grid text-sm" style={{ gridTemplateColumns: "1.7fr 1fr 0.7fr 0.7fr 0.7fr 1.5fr" }}>
            {ALERT_COLS.map((h, idx) => (
              <div key={h} className={`bg-slate-50 px-3 py-2.5 text-xs font-semibold text-slate-500 flex items-center ${idx <= 1 ? "justify-start" : "justify-center"}`}>{h}</div>
            ))}
            {flagged.map((r, i) => {
              const hoursRed = r.flags.includes("hours");
              const rejRed = r.flags.includes("rejected");
              const base = "px-3 py-2.5 border-t border-slate-100 flex items-center";
              return (
                <Fragment key={r.employee_id || i}>
                  <div data-testid={`perf-alert-${r.employee_id || i}`} className={`${base} font-semibold text-slate-800`}>{r.name}</div>
                  <div className={`${base} text-slate-500`}>{r.branch}</div>
                  <div className={`${base} justify-center font-mono-nums font-semibold ${hoursRed ? "text-rose-600" : "text-slate-600"}`}>{typeof r.actual_hours === "number" ? Number(r.actual_hours.toFixed(1)) : (r.actual_hours ?? "—")}</div>
                  <div className={`${base} justify-center font-mono-nums font-semibold ${rejRed ? "text-rose-600" : "text-slate-600"}`}>{r.rejected}</div>
                  <div className={`${base} justify-center font-mono-nums text-slate-600`}>{r.completed}</div>
                  <div className={`${base} justify-start`}>
                    <div className="flex gap-1 flex-wrap">
                      {hoursRed && <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5"><Clock className="h-3 w-3" />{t("dash.perfAlerts.hoursBelow", { n: r.thresholds.min_hours })}</span>}
                      {rejRed && <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5"><Package className="h-3 w-3" />{t("dash.perfAlerts.rejectedOver", { n: r.thresholds.max_rejected })}</span>}
                      <WaSendButton employeeId={r.employee_id} employeeName={r.name} refType="performance" testid={`perf-wa-${r.employee_id || i}`} className="!h-6 !w-6"
                        text={`السلام عليكم ${r.name || ""}\nنلاحظ انخفاض أدائك:${hoursRed ? `\n- ساعات العمل ${typeof r.actual_hours === "number" ? Number(r.actual_hours.toFixed(1)) : r.actual_hours} أقل من المطلوب ${r.thresholds.min_hours ?? ""}` : ""}${rejRed ? `\n- الطلبات المرفوضة ${r.rejected} أعلى من الحد ${r.thresholds.max_rejected}` : ""}\nنرجو تحسين الأداء، ونحن جاهزون للمساعدة.`} />
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
